import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileSpreadsheet, Layers, ListChecks, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type AssignmentGroup, type GroupFormOption } from '@/api/resourceCenterApi'
import { useGroupFormOptions, useInvalidateResourceProfiles } from '@/hooks/useResourceCenter'
import { matchFormNames, namesFromRows, parseCsv, type FormImportResult } from '@/lib/groupFormsImport'
import { FormLinkPicker } from './FormLinkPicker'

type Source = 'pick' | 'import'

/**
 * New group (deck slides 5 and 6): a name, and the forms the group works on —
 * picked from the list with the procedure filters, or dropped in as an Excel /
 * CSV list of form names. People join afterwards, from the group's People tab.
 */
export function CreateGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (group: AssignmentGroup) => void
}) {
  const options = useGroupFormOptions(open)
  const invalidate = useInvalidateResourceProfiles()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [forms, setForms] = useState<string[]>([])
  const [source, setSource] = useState<Source>('pick')

  const reset = () => {
    setName('')
    setDescription('')
    setForms([])
    setSource('pick')
    create.reset()
  }

  const create = useMutation({
    mutationFn: () =>
      resourceProfilesApi.createGroup({
        name: name.trim(),
        description: description.trim() || null,
        formFamilyIds: forms,
      }),
    onSuccess: g => {
      void invalidate()
      onCreated(g)
      reset()
      onOpenChange(false)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent
        className='flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl'
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader className='border-b px-6 py-5'>
          <div className='flex items-center gap-3'>
            <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Layers className='h-5 w-5' />
            </span>
            <div>
              <DialogTitle>New group</DialogTitle>
              <DialogDescription>Name the team and link the forms its members work on.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5'>
          <div className='grid gap-4 sm:grid-cols-[1fr_1.6fr]'>
            <label className='space-y-1.5'>
              <span className='text-sm font-medium'>
                Group name <span className='text-destructive'>*</span>
              </span>
              <Input
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='e.g. Micro Team'
                className='h-10 rounded-lg'
              />
            </label>
            <label className='space-y-1.5'>
              <span className='text-sm font-medium'>Description</span>
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder='Optional — what this team does'
                className='h-10 rounded-lg'
              />
            </label>
          </div>

          <div className='space-y-3'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <div>
                <h3 className='text-sm font-semibold'>Forms</h3>
                <p className='text-xs text-muted-foreground'>
                  {forms.length === 0 ? 'None selected yet — you can also add them later.' : `${forms.length} selected`}
                </p>
              </div>
              <div className='inline-flex rounded-lg bg-muted p-0.5'>
                {(
                  [
                    ['pick', 'Pick from list', ListChecks],
                    ['import', 'Import from Excel', FileSpreadsheet],
                  ] as const
                ).map(([v, label, Icon]) => (
                  <button
                    key={v}
                    type='button'
                    aria-pressed={source === v}
                    onClick={() => setSource(v)}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-all',
                      source === v ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className='h-3.5 w-3.5' /> {label}
                  </button>
                ))}
              </div>
            </div>

            {options.error && <p className='text-sm text-destructive'>{options.error.message}</p>}
            {source === 'pick' ? (
              <FormLinkPicker
                options={options.data}
                loading={options.isLoading}
                linked={forms}
                onChange={setForms}
                canManage
                maxHeight='42vh'
              />
            ) : (
              <ImportForms
                ready={options.isSuccess}
                onImport={r => {
                  setForms(prev => [...new Set([...prev, ...r.matched])])
                }}
                forms={options.data?.forms ?? []}
                selected={forms}
                onReview={() => setSource('pick')}
              />
            )}
          </div>
        </div>

        <DialogFooter className='items-center gap-2 border-t bg-muted/30 px-6 py-4'>
          {create.error && <p className='mr-auto text-sm text-destructive'>{create.error.message}</p>}
          <Button
            variant='ghost'
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            Create group{forms.length > 0 && ` with ${forms.length} form${forms.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Read a dropped or chosen .xlsx / .csv file into rows of text. */
async function readRows(file: File): Promise<string[][]> {
  if (/\.csv$|\.txt$/i.test(file.name)) return parseCsv(await file.text())
  // Loaded on demand: the spreadsheet library stays out of the page's bundle.
  const ExcelJS = (await import('exceljs')).default
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return []
  const rows: string[][] = []
  ws.eachRow({ includeEmpty: false }, row => {
    const cells: string[] = []
    for (let c = 1; c <= row.cellCount; c++) cells.push(row.getCell(c).text ?? '')
    rows.push(cells)
  })
  return rows
}

function ImportForms({
  ready,
  forms,
  selected,
  onImport,
  onReview,
}: {
  ready: boolean
  forms: GroupFormOption[]
  selected: string[]
  onImport: (r: FormImportResult) => void
  onReview: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<(FormImportResult & { file: string }) | null>(null)
  const [error, setError] = useState<string | null>(null)

  const take = async (file: File | undefined) => {
    if (!file) return
    if (!/\.(xlsx|csv|txt)$/i.test(file.name)) {
      setError('Use an .xlsx or .csv file.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const names = namesFromRows(await readRows(file))
      if (names.length === 0) {
        setError('No form names found. Put one form name per row, in a column headed “Form” or in the first column.')
        setResult(null)
        return
      }
      const r = matchFormNames(names, forms)
      setResult({ ...r, file: file.name })
      onImport(r)
    } catch {
      setError('That file could not be read. Save it as .xlsx or .csv and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className='space-y-3'>
      <div
        role='button'
        tabIndex={ready ? 0 : -1}
        aria-disabled={!ready}
        aria-label='Drop an Excel or CSV file of form names, or press to choose one'
        onClick={() => ready && input.current?.click()}
        onKeyDown={e => {
          if (!ready || (e.key !== 'Enter' && e.key !== ' ')) return
          e.preventDefault()
          input.current?.click()
        }}
        onDragOver={e => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={e => {
          e.preventDefault()
          setOver(false)
          void take(e.dataTransfer.files[0])
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors',
          over ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40',
          !ready && 'pointer-events-none opacity-60',
        )}
      >
        {busy ? (
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
        ) : (
          <span className='flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary'>
            <Upload className='h-5 w-5' />
          </span>
        )}
        <p className='text-sm font-medium'>Drag an Excel or CSV file here, or click to browse</p>
        <p className='max-w-md text-xs text-muted-foreground'>
          One form name per row — in a column headed “Form”, or the first column. Names are matched to this center’s
          forms, ignoring case.
        </p>
        <input
          ref={input}
          type='file'
          accept='.xlsx,.csv,.txt'
          className='hidden'
          onChange={e => {
            void take(e.target.files?.[0])
            e.target.value = ''
          }}
        />
      </div>

      {error && <p className='rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}
      {result && (
        <div className='space-y-2 rounded-xl border p-4 text-sm'>
          <p>
            <span className='font-medium'>{result.file}</span>:{' '}
            <span className='font-semibold text-emerald-700'>{result.matched.length} matched</span>
            {result.unmatched.length > 0 && (
              <>
                , <span className='font-semibold text-amber-700'>{result.unmatched.length} not found</span>
              </>
            )}
            . {selected.length} form{selected.length === 1 ? '' : 's'} selected in total.
          </p>
          {result.unmatched.length > 0 && (
            <div className='flex flex-wrap gap-1'>
              {result.unmatched.map(n => (
                <span key={n} className='rounded-md bg-amber-50 px-2 py-0.5 text-xs text-amber-800'>
                  {n}
                </span>
              ))}
            </div>
          )}
          <Button size='sm' variant='outline' onClick={onReview}>
            Review the selection
          </Button>
        </div>
      )}
    </div>
  )
}
